import DataLoadingWrapper from '@efex-components/common/DataLoadingWrapper';
import CustomDrawer from '@efex-components/common/Drawer';
import EmptyState from '@efex-components/common/EmptyState/EmptyState';
import TableSkeleton from '@efex-components/common/Skeleton/TableSkeleton';
import { IEnterprisePayment } from '@efex-services/payments/types';
import theme from '@efexteam/mui-theme';
import { useRole } from '@efexteam/permission-manager';
import {
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ThemeProvider,
  Typography,
  useMediaQuery,
} from '@mui/material';
import Payment from '@pages/payment/Payment';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import PayForward from '../PayForward';
import usePaymentTable from './hooks/usePaymentTable';
import PaymentRow from './PaymentRow';
import PaymentsTableStyle from './PaymentsTable.style';
import { IPaymentsTableProps } from './types';

const PaymentsTable = ({
  payableType,
  setIsLoadingPayments,
}: IPaymentsTableProps) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { isReadOnly } = useRole();

  const {
    payables,
    currentPage,
    expanded,
    payForward,
    isDrawerOpened,
    paymentsState,
    userProfile,
    invoiceData,
    pagination,
    handleChangePage,
    handlePayout,
    handleRejectPayable,
    showInvoice,
    renderRateValue,
    renderCompleteRateValue,
    isValidExecutionDate,
    handleExpandClick,
    getStatusName,
    getScheduleDate,
    setPayForward,
    hideDrawer,
    refetchPayments,
    navigateToTransferDetails,
  } = usePaymentTable({
    payableType,
    setIsLoadingPayments,
  });

  if (!payables?.length && !paymentsState.loading) {
    return (
      <EmptyState
        page="payables"
        buttonClassName="w-40"
        wrapperClassName="mx-auto w-full"
        roleId={userProfile?.legalRepresentative.roleId}
        showTitle={false}
      />
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <DataLoadingWrapper
        loadingData={paymentsState.loading}
        customLoader={<TableSkeleton />}
      >
        <TableContainer className={PaymentsTableStyle.tableContainer}>
          <Table className={clsx('mui-table', PaymentsTableStyle.table)}>
            <TableHead className={PaymentsTableStyle.tableHead}>
              <TableRow>
                <TableCell
                  className={PaymentsTableStyle.tableHeaderCell(!isMobile)}
                >
                  <Typography variant="caption">{t('clientName')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">
                    {t('paymentStatus')}
                  </Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('exchangeRate')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('paymentSent')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('amount')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('desAmount')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('paymentDue')}</Typography>
                </TableCell>
                <TableCell className={PaymentsTableStyle.tableHeaderCell()}>
                  <Typography variant="caption">{t('detail')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {payables?.map((payment: IEnterprisePayment) => (
                <PaymentRow
                  key={payment.id}
                  payment={payment}
                  expanded={expanded}
                  handleExpandClick={handleExpandClick}
                  isValidExecutionDate={isValidExecutionDate}
                  getStatusName={getStatusName}
                  renderCompleteRateValue={renderCompleteRateValue}
                  renderRateValue={renderRateValue}
                  getScheduleDate={getScheduleDate}
                  showInvoice={showInvoice}
                  handlePayout={handlePayout}
                  handleRejectPayable={handleRejectPayable}
                  isReadOnly={isReadOnly}
                  payableType={payableType}
                  setPayForward={setPayForward}
                  navigateToTransferDetails={navigateToTransferDetails}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DataLoadingWrapper>
      <div className="flex justify-center p-4 text-center w-full">
        <Pagination
          page={currentPage}
          color="primary"
          count={pagination?.total_pages}
          onChange={(_: any, page: number) => handleChangePage(page)}
        />
      </div>
      {isDrawerOpened && (
        <CustomDrawer
          PaperProps={{ id: 'paymentWrapper' }}
          position="right"
          isOpen
          onClose={hideDrawer}
        >
          <Payment
            onClose={hideDrawer}
            identify={invoiceData.identify}
            origin={invoiceData.origin}
            paymentId={invoiceData.paymentId}
          />
        </CustomDrawer>
      )}
      {payForward && (
        <PayForward
          payment={payForward}
          onClose={() => setPayForward(null)}
          refetchPayments={refetchPayments}
        />
      )}
    </ThemeProvider>
  );
};

export default PaymentsTable;
